import type { Map as MapLibreMap } from 'maplibre-gl'

/* ============================================================================
 * РЕЛЬЕФ И ОБЪЁМ НА КАРТЕ (задача №14)
 *
 * Проблема, найденная при включении 3D: подложка OpenFreeMap «dark» — это
 * плоский стиль. Проверено разбором style.json:
 *   raster-dem источников: 0
 *   terrain в стиле:       отсутствует
 *   слои:                  background/fill/line/symbol, ни одного fill-extrusion
 *
 * Из этого следует ровно одно: без terrain-rgb демки и высоты рельефа
 * fill-extrusion рисовать НЕЧЕМ — MapLibre строит призмы высот от DEM. Поэтому
 * 3D-кнопка включалась, а объёма не появлялось: камера наклонялась, зона
 * оставалась плоской заливкой.
 *
 * РЕШЕНИЕ: добавляем рельеф на лету, не правя чужие стили на их сервере.
 * Тот же источник, что и у нашего модуля рельефа scripts/terrain/dem_model.py
 * (AWS Terrarium, открытые данные):
 *   https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png
 * encoding: terrarium (это обязательное поле — без него высоты не читаются).
 *
 * Почему НЕ отдельный стиль-файл у себя: стиль всё равно тянулся бы с
 * OpenFreeMap, и мы бы зависели от чужого оформления. Инъекция в уже
 * загруженный стиль держит одну подложку и одну точку отказа.
 *
 * Террейн — УЖИВАНИЕ ПРОИЗВОДИТЕЛЬНОСТИ: Terrarium отдаёт PNG, MapLibre
 * декодирует их в GPU. На слабом телефоне это может стоить кадров. Поэтому
 * terrain включается явно и по кнопке, а не молча.
 * ======================================================================== */

/** Ссылка на тайлы рельефа. Совпадает с scripts/terrain/dem_model.py: */
const DEM_TILES = [
  'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png',
]

/** Идентификаторы наших служебных слоёв/источников */
export const TERRAIN_SOURCE = 'utm-terrain-dem'

/**
 * Включить рельеф для объёмного режима. Вызывается из apply3dTo().
 *
 * Если DEM недоступен, 3D включается частично: камера наклоняется, а зоны
 * остаются плоскими. Лучше так, чем ронять карту целиком.
 */
/**
 * Дождаться готовности стиля.
 *
 * ⚠️ Почему не `isStyleLoaded()` как guard (и почему не 4-секундный таймаут):
 * замер показал, что сразу после нажатия «3D» isStyleLoaded() == false,
 * потом снова true — но на десятой секунде. Проверка через единичный
 * styledata + таймаут 4 с этого не успевала, и рельеф не включался вовсе.
 * Поэтому ждём ПОЛИНГОМ: смена состояния стиля может быть многоэтапной
 * (источник -> спрайт -> глифы), и событие приходит несколько раз.
 *
 * isStyleLoaded() — это style.loaded(), а не «стиль распарсен»: у maplibre
 * здесь учитываются ещё спрайты и глифы. На нашем стиле спрайт частично
 * отсутствует (предупреждение circle-11), поэтому состояние колеблется.
 */
async function waitStyleReady(map: MapLibreMap, budgetMs = 20000): Promise<boolean> {
  const deadline = Date.now() + budgetMs
  while (Date.now() < deadline) {
    if (map.isStyleLoaded?.()) return true
    await new Promise<void>((resolve) => globalThis.setTimeout(resolve, 250))
  }
  return Boolean(map.isStyleLoaded?.())
}

export async function enableTerrain(map: MapLibreMap): Promise<boolean> {
  try {
    // Раньше здесь стоял `if (!map.isStyleLoaded?.()) return false`, и этот
    // молчаливый выход ломал 3D: pitch становился 55°, а рельефа не было.
    if (!(await waitStyleReady(map))) {
      console.warn('[ИВО] рельеф не включился: стиль не стал готов за отведённое время')
      return false
    }

    // Источник DEM нужен один раз — при повторном нажатии «3D» не плодим дубли
    if (!map.getSource(TERRAIN_SOURCE)) {
      map.addSource(TERRAIN_SOURCE, {
        type: 'raster-dem',
        tiles: DEM_TILES,
        // Terrarium, не mapbox: у terrarium своя формула в RGBA
        encoding: 'terrarium',
        tileSize: 256,
        maxzoom: 14,
      })
    }

    // ⚠️ ОТДЕЛЬНОГО hillshade-СЛОЯ НЕТ — и это не недосмотр, а следствие
    // разбора кадра. Первый вариант добавлял слой теней рельефа поверх подложки,
    // и в 3D карта превращалась в чёрный фон с серыми разводами: hillshade
    // непрозрачен и перекрывает всё, что под ним. Вариант «сделать его
    // полупрозрачным» лишний: при включённом setTerrain() maplibre освещает
    // рельеф сам (стиль задаёт light), отдельный слой теней не нужен.
    // Равнинная местность вокруг Москвы (перепад ~20 м) всё равно не дала бы
    // читаемости — объём читается по граням объёмных зон.
    //
    // Единственное, что нужно помимо DEM, — сам terrain:
    map.setTerrain({ source: TERRAIN_SOURCE, exaggeration: 1 })
    return true
  } catch (err) {
    // Ошибку НЕ глотаем молча. Первый прогон показал hasTerrain=false при
    // исправном pitch, и причину невозможно было назвать, потому что здесь
    // стоял пустой catch. Теперь причина видна в консоли браузера.
    console.warn('[ИВО] рельеф не включился:', (err as Error)?.message || err)
    return false
  }
}

/** Выключить рельеф и вернуть плоскую карту. */
export function disableTerrain(map: MapLibreMap): void {
  try {
    if (!map.isStyleLoaded?.()) return
    map.setTerrain(null)
    // Источник оставляем: он уже скачан, повторное включение будет мгновенным.
    // Удалять его — значит платить за скачивание DEM при каждом переключении.
  } catch {
    /* карта уже разобрана — нечего трогать */
  }
}